import { useStoreTypeOptions } from "@faire/deprecated-retailer-visitor-shared/features/SignUp/useStoreTypeOptions";
import { OtherStoreDescriptionField } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_components/fields/storeFields";
import { SignUpPill } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_components/SignUpPill";
import { useSignUpEventTracker } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_hooks/useSignUpEventTracker";
import { Step } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/Step";
import { ISignUpRequest } from "@faire/web-api--source/indigofair/data/ISignUpRequest";
import { useStrictLocalization } from "@faire/web/common/localization";
import { StoreType } from "@faire/web/common/StoreType";
import { Button } from "@faire/web/slate/Button";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useDelayedCallback } from "@faire/web/ui/hooks/useDelayedCallback";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Form, useFormikContext } from "formik";
import isUndefined from "lodash/isUndefined";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import * as Yup from "yup";

export type StoreTypeData = Pick<ISignUpRequest, "style_quiz">;

export const StoreTypeForm: FC = () => {
  const storeTypeOptions = useStoreTypeOptions();
  const { trackFieldValue } = useSignUpEventTracker(Step.STORE_TYPE_S1);
  const { values, handleSubmit, isValid, setFieldValue, initialValues, dirty } =
    useFormikContext<StoreTypeData>();
  const [shouldAutoProgress, setShouldAutoProgress] = useState(
    isUndefined(initialValues.style_quiz?.store_type)
  );
  const storeType = values.style_quiz?.store_type;
  const delayedHandleSubmit = useDelayedCallback(handleSubmit);
  const otherSelected = storeType === StoreType.OTHER;

  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (shouldAutoProgress && storeType !== undefined && !otherSelected) {
      delayedHandleSubmit(800);
    }
  }, [
    delayedHandleSubmit,
    shouldAutoProgress,
    handleSubmit,
    storeType,
    otherSelected,
  ]);

  const handleSelect = useCallback(
    (type: StoreType) => {
      trackFieldValue("storeType", type);
      setFieldValue("style_quiz.store_type", type);
      if (type === StoreType.OTHER && shouldAutoProgress) {
        setShouldAutoProgress(false);
      }
      if (!shouldAutoProgress) {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    },
    [setFieldValue, shouldAutoProgress, trackFieldValue]
  );

  return (
    <Form
      onSubmit={handleSubmit}
      style={{ display: "flex", flexDirection: "column" }}
    >
      <Row gap={12} style={{ flexWrap: "wrap" }}>
        {storeTypeOptions.map((option) => (
          <StyledPill
            key={option.value}
            dataTestId={`PILL_${option.value}`}
            title={option.label}
            isSelected={option.value === storeType}
            onClick={() => handleSelect(option.value)}
          />
        ))}
      </Row>
      {otherSelected ? (
        <>
          <Spacer height="4x" />
          <OtherStoreDescriptionField trackChange={trackFieldValue} />
        </>
      ) : null}
      <Spacer height={{ mobileAndAbove: "4x", tabletAndAbove: "5x" }} />
      {shouldAutoProgress ? null : (
        <>
          <Button
            data-test-id="next"
            disabled={
              !isValid || (!dirty && !initialValues.style_quiz?.store_type)
            }
            type="submit"
          >
            <StrictLocalMsg
              id="m853T8"
              defaultMessage="Next"
              description={{ text: "Button to visit the next page" }}
            />
          </Button>
          <div ref={bottomRef} />
        </>
      )}
    </Form>
  );
};

export const useSchema = () => {
  const { strictLocalize } = useStrictLocalization();
  return Yup.object().shape({
    style_quiz: Yup.object().shape({
      store_type: Yup.string().required(
        strictLocalize({
          id: "PPLP3R",
          defaultMessage: "Please select an option",
        })
      ),
      other_store_type: Yup.string().when("store_type", {
        is: StoreType.OTHER,
        then: (schema) =>
          schema.required(
            strictLocalize({
              id: "0ajvN4",
              defaultMessage: "Please enter your store description",
            })
          ),
      }),
    }),
  });
};

const StyledPill = styled(SignUpPill)`
  width: 100%;
  flex-shrink: 0;

  ${responsiveMediaQueries.tabletAndAbove} {
    width: 50%;
  }
`;
